/*
 * A-Z index filtering, shared by the brand index and A-Z index sections.
 * All links are rendered server-side; this only shows and hides them.
 *
 * Markup contract (data attributes):
 *   [data-az-index]                Root. Add data-az-build-groups when items are
 *                                  rendered flat and need grouping by letter here.
 *   [data-az-list]                 Container for the groups.
 *   [data-az-group="A"]            A letter group (server-rendered, or cloned from
 *                                  the <template data-az-group-template> in the list,
 *                                  which holds [data-az-group-title] and [data-az-group-items]).
 *   [data-az-item]                 One row. data-letter, data-name (searched), plus any
 *                                  attribute a filter matches on (e.g. data-category).
 *   [data-az-filter="letter"]      Chip. data-value is the value to match, "all" resets.
 *                                  Optional data-label / data-label-empty are applied as
 *                                  aria-label; data-az-hide-empty hides the chip when empty.
 *   [data-az-search]               Optional search input.
 *   [data-az-empty]                Shown when nothing matches.
 *   [data-az-status]               Optional live region; data-template uses [count].
 */
(() => {
  const ALL = 'all';

  const normalize = (value) =>
    (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  const buildGroups = (root, list, items) => {
    const template = list.querySelector('template[data-az-group-template]');
    if (!template) return;

    // Group order follows the letter chips, so each page controls where '#' / '&' go.
    const letterOrder = Array.from(root.querySelectorAll('[data-az-filter="letter"]'))
      .map((chip) => chip.dataset.value)
      .filter((value) => value !== ALL);

    const sorted = [...items].sort((a, b) => a.name.localeCompare(b.name));
    const fragment = document.createDocumentFragment();

    letterOrder.forEach((letter) => {
      const groupItems = sorted.filter((item) => item.el.dataset.letter === letter);
      if (!groupItems.length) return;

      const group = template.content.firstElementChild.cloneNode(true);
      group.dataset.azGroup = letter;
      group.querySelector('[data-az-group-title]').textContent = letter;
      const slot = group.querySelector('[data-az-group-items]') || group;
      groupItems.forEach((item) => slot.appendChild(item.el));
      fragment.appendChild(group);
    });

    list.replaceChildren(template, fragment);
  };

  const init = (root) => {
    if (root.dataset.azReady) return;
    root.dataset.azReady = 'true';

    const list = root.querySelector('[data-az-list]') || root;
    const items = Array.from(root.querySelectorAll('[data-az-item]')).map((el) => ({
      el,
      name: normalize(el.dataset.name),
    }));

    if ('azBuildGroups' in root.dataset) buildGroups(root, list, items);

    const groups = Array.from(root.querySelectorAll('[data-az-group]'));
    items.forEach((item) => { item.group = item.el.closest('[data-az-group]'); });
    const chips = Array.from(root.querySelectorAll('[data-az-filter]'));
    const filterKeys = [...new Set(chips.map((chip) => chip.dataset.azFilter))];
    const search = root.querySelector('[data-az-search]');
    const empty = root.querySelector('[data-az-empty]');
    const status = root.querySelector('[data-az-status]');

    const state = { query: '' };
    filterKeys.forEach((key) => { state[key] = ALL; });

    const hasFilterValue = (item, key, value) =>
      (item.el.dataset[key] || '').split(',').includes(value);

    // Chips with no matching items are disabled (and optionally hidden).
    chips.forEach((chip) => {
      const { azFilter: key, value } = chip.dataset;
      if (value === ALL) return;
      const hasItems = items.some((item) => hasFilterValue(item, key, value));
      chip.disabled = !hasItems;
      if ('azHideEmpty' in chip.dataset) chip.style.display = hasItems ? '' : 'none';
      const label = hasItems ? chip.dataset.label : chip.dataset.labelEmpty;
      if (label) chip.setAttribute('aria-label', label);
    });

    const matchesFilter = (item, key) =>
      state[key] === ALL || hasFilterValue(item, key, state[key]);

    const matches = (item) =>
      filterKeys.every((key) => matchesFilter(item, key)) &&
      (!state.query || item.name.includes(state.query));

    const apply = () => {
      let total = 0;
      const visibleGroups = new Set();
      items.forEach((item) => {
        const show = matches(item);
        item.el.style.display = show ? '' : 'none';
        if (show) {
          total++;
          visibleGroups.add(item.group);
        }
      });

      groups.forEach((group) => {
        group.style.display = visibleGroups.has(group) ? '' : 'none';
      });

      if (empty) empty.style.display = total ? 'none' : '';
      if (status && status.dataset.template) {
        status.textContent = status.dataset.template.replace('[count]', total);
      }
    };

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        if (chip.disabled) return;
        const { azFilter: key, value } = chip.dataset;
        // Clicking the active chip again resets that filter to "All".
        state[key] = state[key] === value ? ALL : value;

        chips
          .filter((other) => other.dataset.azFilter === key)
          .forEach((other) => {
            const isActive = other.dataset.value === state[key];
            other.classList.toggle('active', isActive);
            other.setAttribute('aria-pressed', isActive);
          });
        apply();
      });
    });

    if (search) {
      search.addEventListener('input', () => {
        state.query = normalize(search.value);
        apply();
      });
    }

    apply();
  };

  const initAll = () => document.querySelectorAll('[data-az-index]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
